import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { getActionAriaLabelSync } from '@/utils/ariaLabels';

import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useIsMdDown } from '@/hooks/useMedia';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { Maybe } from '@/lib/types';

import { formatTime, getVideoSettings } from './helper';

import { ReactComponent as ExpandSvg } from '@/assets/icons/video/expand.svg';
import { ReactComponent as MuteSvg } from '@/assets/icons/video/mute.svg';
import { ReactComponent as MutedSvg } from '@/assets/icons/video/muted.svg';
import { ReactComponent as PauseSvg } from '@/assets/icons/video/pause.svg';
import { ReactComponent as PlaySvg } from '@/assets/icons/video/play.svg';
import { ReactComponent as ShrinkSvg } from '@/assets/icons/video/shrink.svg';

import * as S from './VideoPlayer.styles';

interface VideoProps {
  controls?: boolean;
  desktopAspectRatio?: { x: number; y: number };
  isCountDownType?: boolean;
  isPopupMode?: boolean;
  lazyLoad?: boolean;
  lazyLoadControlled?: boolean;
  localeCode?: string;
  mediaUrl: Maybe<string>;
  mobileAspectRatio?: { x: number; y: number };
  muted?: boolean;
  onClose?: () => void;
  playCallback?: () => void;
  playOption: string;
  poster?: string;
  setLoadingState?: (state: string) => void;
  title?: Maybe<string>;
}

const attemptVideoPlayback = (video: HTMLVideoElement) => {
  const playPromise = video.play();
  if (playPromise !== undefined) {
    void playPromise.catch(() => {
      // Playback is best-effort and can be blocked by the browser's autoplay policy.
    });
  }
};

export const VideoPlayerBase = ({
  controls = false,
  desktopAspectRatio,
  isCountDownType,
  isPopupMode,
  lazyLoad = false,
  lazyLoadControlled = false,
  localeCode = 'en-US',
  mediaUrl,
  mobileAspectRatio,
  muted = true,
  onClose,
  playCallback,
  playOption,
  poster,
  setLoadingState,
  title,
}: VideoProps) => {
  const { sendEvent } = useGoogleAnalytics();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [percent, setPercent] = useState(0);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isMobileView = useIsMdDown();

  const videoRef = useRef<HTMLVideoElement>(null);
  const progressBarRef = useRef<HTMLProgressElement>(null);
  const [autoPlay, setAutoPlay] = useState<boolean>(false);
  const [loop, setLoop] = useState<boolean>(true);

  // track analytics fired milestones to avoid duplicates
  const firedMilestonesRef = useRef<Set<number>>(new Set());
  const playStartedRef = useRef<boolean>(false);
  const [videoPlayButtonRoot, setVideoPlayButtonRoot] =
    useState<HTMLElement | null>(null);

  // ------ lazy loading with IntersectionObserver ------
  useEffect(() => {
    if (lazyLoadControlled || !lazyLoad || isVideoLoaded) return;

    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVideoLoaded(true);
          observer.disconnect();
        }
      },
      { rootMargin: '50px' },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, [lazyLoad, lazyLoadControlled, isVideoLoaded]);

  useEffect(() => {
    if (!lazyLoad) {
      setIsVideoLoaded(true);
      return;
    }

    if (!lazyLoadControlled) {
      return;
    }

    setIsVideoLoaded(false);
    videoRef.current?.pause();
    setIsPlaying(false);
  }, [lazyLoad, lazyLoadControlled]);

  // ------ sync autoPlay/loop from playOption ------
  useEffect(() => {
    const { autoPlay, loop } = getVideoSettings(playOption || 'PlayOnClick');
    setAutoPlay(autoPlay);
    setLoop(loop);
  }, [playOption]);

  // ------ keep video DOM muted in sync with isMuted state ------
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
  }, [isMuted]);

  // ------ load mediaUrl and try autoplay when required ------
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (lazyLoad) {
      if (!isVideoLoaded) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
      setLoadingState?.('loading');
      return;
    }

    if (!isVideoLoaded) {
      setLoadingState?.('loading');
      return;
    }

    // reset analytics milestones when media changes
    firedMilestonesRef.current.clear();
    playStartedRef.current = false;
    setPercent(0);
    setCurrentTime(0);
    setDuration(0);

    setLoadingState?.('loading');

    const onLoaded = () => {
      setLoadingState?.('loaded');
      setDuration(video.duration || 0);
    };
    const onCanPlay = () => {
      if (autoPlay) {
        attemptVideoPlayback(video);
      }
    };
    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('loadeddata', onLoaded);
    video.addEventListener('canplay', onCanPlay);

    video.load();

    if (autoPlay) {
      if (!isMuted) {
        video.muted = true;
        setIsMuted(true);
      }
      attemptVideoPlayback(video);
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('loadeddata', onLoaded);
      video.removeEventListener('canplay', onCanPlay);
    };
  }, [mediaUrl, autoPlay, lazyLoad, isVideoLoaded]);

  // ------ keep isPlaying in sync with actual playback events ------
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onPlay = () => {
      setIsPlaying(true);
      if (!playStartedRef.current) {
        sendEvent?.({
          event: EVENT_NAME.VIDEO_START,
          video_title: title as string,
          video_url: mediaUrl as string,
        });
        playStartedRef.current = true;
      }
    };
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      // fire complete if not already
      if (!firedMilestonesRef.current.has(100)) {
        sendEvent?.({
          event: EVENT_NAME.VIDEO_COMPLETE,
          video_title: title as string,
          video_url: mediaUrl as string,
        });
        firedMilestonesRef.current.add(100);
      }
    };
    const onTimeUpdate = () => setCurrentTime(v.currentTime);

    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);
    v.addEventListener('timeupdate', onTimeUpdate);

    return () => {
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
      v.removeEventListener('timeupdate', onTimeUpdate);
    };
  }, [mediaUrl, title, sendEvent]);

  // ------ percent calculation with guards ------
  useEffect(() => {
    if (!duration || duration <= 0) {
      setPercent(0);
      return;
    }
    const p = Math.max(
      0,
      Math.min(100, Math.floor((currentTime / duration) * 100)),
    );
    setPercent(p);
  }, [currentTime, duration]);

  // ------ analytics milestones (deduped) ------
  useEffect(() => {
    const milestones = [25, 50, 75];
    if (firedMilestonesRef.current.has(percent)) return;
    if (milestones.includes(percent)) {
      sendEvent?.({
        event: EVENT_NAME.VIDEO_PROGRESS,
        video_title: title as string,
        video_url: mediaUrl as string,
        video_percent: percent,
      });
      firedMilestonesRef.current.add(percent);
    } else if (percent === 100 && !firedMilestonesRef.current.has(100)) {
      sendEvent?.({
        event: EVENT_NAME.VIDEO_COMPLETE,
        video_title: title as string,
        video_url: mediaUrl as string,
      });
      firedMilestonesRef.current.add(100);
    }
  }, [percent, mediaUrl, title, sendEvent]);

  // ------ seeking: pointer-based drag handling (works for mouse + touch) ------
  useEffect(() => {
    const onPointerMove = (e: PointerEvent) => {
      if (!isSeeking) return;
      const bar = progressBarRef.current;
      const v = videoRef.current;
      if (!bar || !v) return;
      const rect = bar.getBoundingClientRect();
      const offsetX = (e.clientX ?? 0) - rect.left;
      const ratio = Math.max(0, Math.min(1, offsetX / rect.width));
      v.currentTime = ratio * Math.max(0, duration || 0);
    };

    const onPointerUp = () => {
      setIsSeeking(false);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    return () => {
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [isSeeking, duration]);

  const seekByEvent = useCallback(
    (clientX: number) => {
      const bar = progressBarRef.current;
      const v = videoRef.current;
      if (!bar || !v) return;
      const rect = bar.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const ratio = Math.max(0, Math.min(1, offsetX / rect.width));
      v.currentTime = ratio * Math.max(0, duration || 0);
    },
    [duration],
  );

  const handleProgressClick = useCallback(
    (e: React.MouseEvent<HTMLProgressElement>) => {
      e.stopPropagation();
      const clientX = (e.nativeEvent as MouseEvent).clientX;
      seekByEvent(clientX);
    },
    [seekByEvent],
  );

  // ------ play/pause toggle
  const togglePlayPause: React.MouseEventHandler<HTMLElement> = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault(); // important if parent is link
      const video = videoRef.current;
      if (!video) return;

      if (playOption === 'OpenPopup' && !isPopupMode) {
        playCallback?.();
        return;
      }

      if (video.paused) {
        attemptVideoPlayback(video);
      } else {
        video.pause();
      }
    },
    [playCallback, isPopupMode, playOption, mediaUrl, title, sendEvent],
  );

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const savedOverflowRef = useRef<string>('');

  const toggleFullscreen = () => {
    const element = document.documentElement;
    if (!document.fullscreenElement) {
      savedOverflowRef.current = document.body.style.overflow;
      element.requestFullscreen?.().then(() => {
        setIsFullscreen(true);
        document.body.style.overflow = 'hidden';
      });
    } else {
      document.exitFullscreen?.().then(() => {
        setIsFullscreen(false);
        document.body.style.overflow = savedOverflowRef.current;
      });
    }
  };

  // Sync isFullscreen state and restore overflow when the browser exits
  // fullscreen via Esc key or other native paths (not our toggle button).
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
        document.body.style.overflow = savedOverflowRef.current;
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Cleanup: restore overflow if unmounted while still in fullscreen
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      }
      document.body.style.overflow = savedOverflowRef.current;
    };
  }, []);

  useEffect(() => {
    if (isPopupMode && videoRef.current) {
      attemptVideoPlayback(videoRef.current);
    }
  }, [isPopupMode]);

  const classes = useMemo(() => {
    return [
      'play-button-center',
      playOption === 'OpenPopup' && !isPlaying && 'show',
      playOption === 'PlayOnClick' && !isMobileView && !isPlaying && 'show',
      playOption === 'PlayOnClick' && isMobileView && !isPlaying && 'show',
    ]
      .filter(Boolean)
      .join(' ');
  }, [playOption, isPlaying, isMobileView]);

  useEffect(() => {
    if (!isCountDownType) {
      setVideoPlayButtonRoot(null);
      return;
    }

    const root = document.getElementById('video-play-button-root');
    setVideoPlayButtonRoot(root);
    if (root) {
      root.className = classes;
    }
  }, [isCountDownType, classes]);

  const renderPlayButton = (
    <S.PlayButtonCenter
      aria-label={
        getActionAriaLabelSync('play_video', localeCode) || 'Play video'
      }
      className={classes}
      data-testid='videoPlayButtonCenter'
      onClick={togglePlayPause}
    >
      <S.PlayIconLarge />
    </S.PlayButtonCenter>
  );

  return (
    <S.PlayerWrapper
      data-playing={isPlaying ? 'true' : 'false'}
      data-testid='VideoPlayerWrapper'
    >
      {['OpenPopup', 'PlayOnClick'].includes(playOption) && !isPlaying && (
        <S.PlayerOverlay
          className={[
            'player-overlay',
            playOption === 'OpenPopup' && !isPlaying && 'show',
            playOption === 'PlayOnClick' && isPlaying && 'hover',
            playOption === 'PlayOnClick' && !isPlaying && 'show',
          ]
            .filter(Boolean)
            .join(' ')}
        ></S.PlayerOverlay>
      )}
      {isPopupMode && (
        <S.CloseButton
          aria-label={
            getActionAriaLabelSync('close_video', localeCode) || 'Close video'
          }
          data-testid='VideoCloseButton'
          onClick={onClose}
        >
          ✕
        </S.CloseButton>
      )}
      {isCountDownType && videoPlayButtonRoot
        ? createPortal(renderPlayButton, videoPlayButtonRoot)
        : renderPlayButton}

      {playOption === 'AutoplayLoop' && !isPlaying && (
        <S.PlayButtonCorner
          aria-label={
            getActionAriaLabelSync('play_video', localeCode) || 'Play video'
          }
          data-testid='videoPlayButtonCorner'
          onClick={togglePlayPause}
        >
          <S.PlayIconSmall />
        </S.PlayButtonCorner>
      )}
      {playOption === 'AutoplayLoop' && isPlaying && (
        <S.PauseButtonCorner
          aria-label={
            getActionAriaLabelSync('pause_video', localeCode) || 'Pause video'
          }
          data-testid='videoPauseButtonCorner'
          onClick={togglePlayPause}
        >
          <S.PauseIconLarge />
        </S.PauseButtonCorner>
      )}
      <S.PlayerVideo
        ref={videoRef}
        $desktopAspectRatio={desktopAspectRatio}
        $mobileAspectRatio={mobileAspectRatio}
        autoPlay={autoPlay}
        controls={controls}
        data-testid='videoPlayer'
        loop={loop}
        muted={muted}
        onClick={togglePlayPause}
        playsInline
        poster={poster}
        preload='none'
      >
        {isVideoLoaded && (
          <>
            <source src={mediaUrl ?? ''} type='video/mp4' />
            <source src={mediaUrl ?? ''} type='video/webm' />
          </>
        )}
      </S.PlayerVideo>
      <S.PlayerControls
        className={[
          'controls',
          playOption === 'PlayOnClick' &&
            isMobileView &&
            currentTime > 0 &&
            'show',
          playOption === 'PlayOnClick' && !isMobileView && 'hover',
          playOption === 'PlayOnClick' &&
            !isMobileView &&
            currentTime > 0 &&
            !isPlaying &&
            'show',
          playOption === 'OpenPopup' && isPopupMode && 'show',
          playOption === 'AutoplayLoop' && 'hide',
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid='VideoPlayerControls'
      >
        <S.PlayersControlsProgressBar>
          <S.StyledProgress
            ref={progressBarRef}
            data-testid='VideoProgressBar'
            max={duration}
            onClick={handleProgressClick}
            onMouseDown={() => setIsSeeking(true)}
            onMouseUp={() => setIsSeeking(false)}
            value={currentTime}
          />
        </S.PlayersControlsProgressBar>
        <S.ControlRow>
          <S.ButtonGroup>
            <S.StyledPlayAndPauseButton
              aria-label={
                isPlaying
                  ? getActionAriaLabelSync('pause_video', localeCode) ||
                    'Pause video'
                  : getActionAriaLabelSync('play_video', localeCode) ||
                    'Play video'
              }
              data-testid='VideoPlayAndPause'
              onClick={togglePlayPause}
              type='button'
            >
              {isPlaying ? (
                <PauseSvg aria-hidden='true' />
              ) : (
                <PlaySvg aria-hidden='true' />
              )}
            </S.StyledPlayAndPauseButton>
            <S.ProgressText>
              {formatTime(currentTime)} / {formatTime(duration)}
            </S.ProgressText>
          </S.ButtonGroup>
          <S.ButtonGroup>
            <S.StyledMuteButton
              aria-label={
                isMuted
                  ? getActionAriaLabelSync('unmute_video', localeCode) ||
                    'Unmute video'
                  : getActionAriaLabelSync('mute_video', localeCode) ||
                    'Mute video'
              }
              onClick={toggleMute}
              type='button'
            >
              {isMuted ? (
                <MutedSvg aria-hidden='true' />
              ) : (
                <MuteSvg aria-hidden='true' />
              )}
            </S.StyledMuteButton>
            {playOption === 'OpenPopup' && (
              <S.StyledToggleScreenButton
                aria-label={
                  isFullscreen
                    ? getActionAriaLabelSync('exit_fullscreen', localeCode) ||
                      'Exit fullscreen'
                    : getActionAriaLabelSync('enter_fullscreen', localeCode) ||
                      'Enter fullscreen'
                }
                onClick={toggleFullscreen}
                type='button'
              >
                {isFullscreen ? (
                  <ShrinkSvg aria-hidden='true' />
                ) : (
                  <ExpandSvg aria-hidden='true' />
                )}
              </S.StyledToggleScreenButton>
            )}
          </S.ButtonGroup>
        </S.ControlRow>
      </S.PlayerControls>
    </S.PlayerWrapper>
  );
};
